import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Shield, CheckCircle, XCircle, Search, Calendar, Building2, FileText, User, AlertTriangle } from 'lucide-react';

interface VerificationResult {
  isValid: boolean;
  error?: string;
  certification?: any;
  company?: { id: number; name: string };
  framework?: { id: number; name: string; version: string };
  auditor?: { id: number; first_name: string; last_name: string };
}

const VerifyCertificate: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const [verificationCode, setVerificationCode] = useState(code || '');
  const [result, setResult] = useState<VerificationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    if (code) {
      handleVerify(code);
    }
  }, [code]);

  async function handleVerify(codeToVerify?: string) {
    const searchCode = codeToVerify || verificationCode.trim();
    if (!searchCode) return;

    setLoading(true);
    setSearched(true);
    try {
      const res = await fetch(`/api/compliance/certifications/verify/${searchCode}`);
      const data = await res.json();
      setResult(data.data || data);
    } catch {
      setResult({ isValid: false, error: 'Unable to verify certificate. Please try again.' });
    } finally {
      setLoading(false);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    handleVerify();
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 py-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
              <Shield className="text-white" size={20} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Certificate Verification</h1>
              <p className="text-xs text-slate-500">Verify the authenticity of a compliance certificate</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        {/* Search Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-200 p-6">
          <label className="block text-sm font-medium text-slate-700 mb-2">Verification Code</label>
          <div className="flex gap-3">
            <input
              type="text"
              value={verificationCode}
              onChange={e => setVerificationCode(e.target.value.toUpperCase())}
              placeholder="Enter certificate verification code"
              className="flex-1 px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-mono tracking-wider"
              autoFocus
            />
            <button
              type="submit"
              disabled={loading || !verificationCode.trim()}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm transition-colors disabled:opacity-40 flex items-center gap-2"
            >
              <Search size={16} />
              {loading ? 'Verifying...' : 'Verify'}
            </button>
          </div>
        </form>

        {/* Results */}
        {loading && (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent mx-auto mb-3" />
            <p className="text-slate-500 text-sm">Verifying certificate...</p>
          </div>
        )}

        {!loading && searched && result && (
          <div className={`bg-white rounded-2xl border-2 overflow-hidden ${result.isValid ? 'border-green-200' : 'border-red-200'}`}>
            {/* Status Banner */}
            <div className={`px-6 py-4 flex items-center gap-3 ${result.isValid ? 'bg-green-50' : 'bg-red-50'}`}>
              {result.isValid ? (
                <>
                  <CheckCircle className="text-green-600 shrink-0" size={24} />
                  <div>
                    <p className="text-green-800 font-semibold">Certificate is Valid</p>
                    <p className="text-green-600 text-xs">This certificate has been verified and is currently active.</p>
                  </div>
                </>
              ) : (
                <>
                  {result.error === 'Certificate not found' ? (
                    <AlertTriangle className="text-amber-600 shrink-0" size={24} />
                  ) : (
                    <XCircle className="text-red-600 shrink-0" size={24} />
                  )}
                  <div>
                    <p className={`font-semibold ${result.error === 'Certificate not found' ? 'text-amber-800' : 'text-red-800'}`}>
                      {result.error === 'Certificate not found' ? 'Certificate Not Found' : 'Certificate Invalid'}
                    </p>
                    <p className={`text-xs ${result.error === 'Certificate not found' ? 'text-amber-600' : 'text-red-600'}`}>
                      {result.error || 'This certificate could not be verified.'}
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Certificate Details */}
            {result.isValid && result.certification && (
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <Building2 className="text-slate-400 shrink-0 mt-0.5" size={16} />
                    <div>
                      <p className="text-xs text-slate-500 font-medium">Certified Company</p>
                      <p className="text-sm text-slate-900 font-semibold">{result.company?.name}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <FileText className="text-slate-400 shrink-0 mt-0.5" size={16} />
                    <div>
                      <p className="text-xs text-slate-500 font-medium">Framework</p>
                      <p className="text-sm text-slate-900 font-semibold">{result.framework?.name}</p>
                      {result.framework?.version && (
                        <p className="text-xs text-slate-500">Version {result.framework.version}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Shield className="text-slate-400 shrink-0 mt-0.5" size={16} />
                    <div>
                      <p className="text-xs text-slate-500 font-medium">Certificate Number</p>
                      <p className="text-sm text-slate-900 font-mono">{result.certification.certificate_number}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <User className="text-slate-400 shrink-0 mt-0.5" size={16} />
                    <div>
                      <p className="text-xs text-slate-500 font-medium">Auditor</p>
                      <p className="text-sm text-slate-900">{result.auditor?.first_name} {result.auditor?.last_name}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Calendar className="text-slate-400 shrink-0 mt-0.5" size={16} />
                    <div>
                      <p className="text-xs text-slate-500 font-medium">Issue Date</p>
                      <p className="text-sm text-slate-900">{new Date(result.certification.issue_date).toLocaleDateString('en-GB')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Calendar className="text-slate-400 shrink-0 mt-0.5" size={16} />
                    <div>
                      <p className="text-xs text-slate-500 font-medium">Expiry Date</p>
                      <p className="text-sm text-slate-900">{new Date(result.certification.expiry_date).toLocaleDateString('en-GB')}</p>
                    </div>
                  </div>
                </div>

                <div className="pt-3 border-t border-slate-100">
                  <p className="text-xs text-slate-400">
                    Compliance Score: {result.certification.overall_score}% | Status: {result.certification.status}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-slate-400 pt-4">
          EIS-TECH Certificate Verification
        </div>
      </div>
    </div>
  );
};

export default VerifyCertificate;
